<template>
	<header class="header-nav header-nav--mobile">
		<div id="header" class="header-content">
			<router-link to="/home" class="app-logo" v-if="!isSearching">
				<img fetchpriority="high" :src="logoSrc" alt="Valor Movies" />
			</router-link>

			<!-- <div class="header-actions" :class="{ searching: isSearching }">
				<LangSwitcher :isMobile="true" v-if="!isSearching" />
				<Search @search-opened="isSearching = true" @search-closed="isSearching = false" />
				<profile v-if="isLoggedIn && !isSearching" />
				<img
					v-if="!isLoggedIn && !isSearching"
					@click="triggerLogin"
					class="login-avatar"
					src="../images/login_avatar.webp"
					alt="Login"
				/>
			</div> -->
			<!-- <Transition name="slide-down">
				<nav v-if="showNav && isLoggedIn === true" class="header-subnav full-bleed">
					<router-link to="/movies" class="nav-link">{{ $t("header.movies") }}</router-link>
					<router-link to="/series" class="nav-link">{{ $t("header.series") }}</router-link>
				</nav>
			</Transition> -->
			<div class="header-actions" :class="{ searching: isSearching }">
				<HeaderSectionRight @search-closed="isSearching = false" @search-opened="isSearching = true" />
			</div>
		</div>
	</header>
</template>

<script lang="ts" setup>
	import { computed, ComputedRef, onMounted, ref, watch } from "vue"
	import { useScroll } from "@vueuse/core"
	import HeaderSectionRight from "./header/HeaderSectionRight.vue"
	import profile from "./profileBlock.vue"
	import Search from "./Search.vue"
	import { useUserStore } from "../stores/user"
	import { useLogin } from "../composables/useLogin"
	import LangSwitcher from "./header/LangSwitcher.vue"
	// @ts-ignore This import works correctly
	import logoImage from "../images/valor_logo.svg" // Vite will resolve this path

	import { StatusBar, Style } from "@capacitor/status-bar"

	// iOS only
	// window.addEventListener("statusTap", function () {
	// 	console.log("statusbar tapped")
	// })

	// Display content under transparent status bar
	// StatusBar.setOverlaysWebView({ overlay: true })
	// StatusBar.setBackgroundColor({ color: "#000000" })
	// StatusBar.setStyle({ style: Style.Dark }) // Light text on dark background

	const setStatusBarStyleDark = async () => {
		await StatusBar.setStyle({ style: Style.Dark })
	}

	setStatusBarStyleDark()

	const setStatusBarStyleLight = async () => {
		await StatusBar.setStyle({ style: Style.Light })
	}

	const hideStatusBar = async () => {
		await StatusBar.hide()
	}

	const showStatusBar = async () => {
		await StatusBar.show()
	}

	// hideStatusBar()

	const { login } = useLogin()

	// Dynamically determine the logo source
	const logoSrc = (import.meta.env as any).MODE === "tizen" ? "dist/assets/valor_logo.svg" : logoImage
	const isSearching = ref(false)
	const userStore: ReturnType<typeof useUserStore> = useUserStore()

	const isLoggedIn: ComputedRef<boolean> = computed((): boolean => userStore.state.isLoggedIn)

	const triggerLogin = async (): Promise<void> => {
		sessionStorage.setItem("post_auth_redirect", "/add-profile")
		await login()
	}

	// Detect scroll direction to show/hide navigation
	const { directions, y } = useScroll(window, { throttle: 100 })
	const showNav = ref(false)
	const lastScrollY = ref(0)

	watch(y, (newY) => {
		// Only show nav when scrolling up and past a threshold (50px)
		if (newY > 50 && directions.top) {
			showNav.value = true
		} else if (directions.bottom || newY < 10) {
			showNav.value = false
		}
		lastScrollY.value = newY
	})

	const emit = defineEmits(["mounted"])

	onMounted(() => emit("mounted"))
</script>

<style lang="scss" scoped>
	// .is-android {

	// }
	.is-ios {
		.header-nav {
			padding-top: var(--safe-area-inset-top, 50px);
		}
	}

	// Slide down transition for the subnav
	.slide-down-enter-active,
	.slide-down-leave-active {
		transition: all 0.3s ease-in;
	}

	.slide-down-enter-from {
		opacity: 0;
		max-height: 0;
	}

	.slide-down-enter-to {
		opacity: 1;
		max-height: 100px;
		margin-top: 0;
	}

	.slide-down-leave-from {
		opacity: 1;
		max-height: 100px;
		margin-top: 0;
	}

	.slide-down-leave-to {
		opacity: 0;
		max-height: 0;
	}

	// Add top padding to page content to prevent overlap
	// :global(body.is-ios .page-content),
	// :global(body.is-android .page-content) {
	// 	padding-top: calc(60px + var(--safe-area-inset-top, 0px));
	// }
</style>

<style lang="scss">
	.header-nav {
		position: sticky;
		top: 0;
		left: 0;
		right: 0;
		z-index: 1000;
		background: var(--black);

		// background: rgba(30, 30, 28, 0.95);
		// backdrop-filter: blur(10px);
		// border-bottom: 1px solid rgba(255, 255, 255, 0.1);

		.header-content {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 12px 10px;
			max-width: 100%;
			position: relative;
			z-index: 2;

			.app-logo {
				display: flex;
				align-items: center;

				img {
					width: 140px;
					transition: height 0.3s ease;
				}
			}

			.header-actions {
				display: flex;
				align-items: center;
				gap: 8px;
				position: relative;
				z-index: 3;

				&.searching {
					width: 100%;
					.section--right {
						width: 100%;
					}
					.search-wrapper {
						width: 100%;
					}
					.input-wrapper {
						width: 100%;
					}
				}

				@include br.tablet-down {
					gap: 26px;
				}
				@include br.respond-to(br.$mobile-xlarge) {
					gap: 20px;
				}

				.login-avatar {
					width: 36px;
					height: 36px;
					border-radius: 50%;
					object-fit: cover;
					object-position: center;
					background: var(--gray);
					cursor: pointer;
					transition: transform 0.2s ease;

					@include br.tablet-down {
						width: 26px;
						height: 26px;
					}

					&:hover,
					&:focus {
						transform: scale(1.1);
					}
				}
			}
		}

		.header-subnav {
			display: flex;
			align-items: stretch;
			gap: 10px;
			padding: 0 18px 18px 18px;
			overflow-x: auto;
			scrollbar-width: none; // Firefox
			width: 100%;
			background: var(--black);
			position: absolute;
			top: 100%;
			z-index: 9999;

			&::-webkit-scrollbar {
				display: none; // Chrome, Safari
			}

			.nav-link {
				flex: 1;
				text-align: center;
				color: rgba(255, 255, 255, 0.7);
				text-decoration: none;
				font-size: 14px;
				font-weight: 500;
				white-space: nowrap;
				transition: color 0.2s ease;
				position: relative;
				background: #50595d;
				padding: 5px 10px;
				border-radius: 7px;

				&:hover,
				&:focus {
					color: rgba(255, 255, 255, 1);
				}

				&.router-link-active {
					color: #50595d;
					background: white;
				}
			}
		}

		// Hide on larger screens (desktop)
		// @media (min-width: 1024px) {
		// 	display: none;
		// }
	}
</style>
